import { DateTime } from 'luxon';
import React, { FunctionComponent, useState, useEffect, useMemo } from 'react';
import {
  Button,
  Dropdown,
  DropdownButton,
  FormCheck,
  Row,
} from 'react-bootstrap';
import { connect, ConnectedProps } from 'react-redux';
import { toast } from 'react-toastify';
import apis from '../../apis';
import {
  DateTimePicker,
  EditableProperties,
  EditableTableDiv,
  HeaderDisplayKeysWithType,
} from '../../components';
import { ClassSem, WrapClassBaseLayout } from '../../layouts';
import { PHITee, Student, StudentGradeNoSeat, UserInfo } from '../../model';
import { ErrorDispatches } from '../../redux/Dispatches';
import { ApplicationState } from '../../redux/States';
import { Nullable } from '../../types';
import { TeeTableHeader } from '../TableHeaders/TeeHeader';
import {
  downloadDataAsExcel,
  downloadDataAsPDFByPrint,
  SheetHeaderDisplay,
} from '../../utils';

type PageData = StudentGradeNoSeat & Nullable<PHITee> & { index: number };
type TeePageData = StudentGradeNoSeat & Nullable<PHITee> & EditableProperties;

const modalDisplayHeader: HeaderDisplayKeysWithType<TeePageData>[] = [
  { property: 'seat', display: '座號' },
  { property: 'name', display: '學生' },
  ...(TeeTableHeader as HeaderDisplayKeysWithType<TeePageData>[]),
];
const excelHeader: (SheetHeaderDisplay<PageData> & HeaderDisplayKeysWithType<PageData>)[] = [
  {
    display: '序號',
    property: 'index',
    onSheetRender: (_value, idx) => idx + 1,
  },
  { display: '統編', property: 'pid' },
  { display: '學生', property: 'name' },
  {
    display: '性別',
    property: 'sex',
    onSheetRender: (v) => {
      return v ? (v == 1 ? "男" : "女") : ""
    }
  },
  { display: '年級', property: 'grade' },
  {
    display: '班級',
    property: 'className',
    onRender: (value: unknown) => {
      const num = parseInt(value as string, 10);
      return !isNaN(num) && num > 100 && num < 200 ? num - 100 : value;
    },
    onSheetRender: (value) => {
      const num = parseInt(value as string, 10);
      if (!isNaN(num)) {
        return num > 100 && num < 200 ? num - 100 : num;
      }
      return value?.toString() ?? '';
    },
  },
  { display: '座號', property: 'seat' },
  { display: '未治療齲齒', property: 't01' },
  { display: '已治療齲齒', property: 't11' },
  { display: '上顎恆牙第一大臼齒齲齒經驗', property: 't12' },
  { display: '下顎恆牙第一大臼齒齲齒經驗', property: 't13' },
  { display: '恆牙臼齒窩溝封填', property: 't15' },
  { display: '口腔衛生不良', property: 't04' },
  { display: '咬合不正', property: 't07' },
  { display: '牙結石', property: 't05' },
  { display: '牙齦炎', property: 't08' },
  { display: '口腔黏膜異常', property: 't09' },
  { display: '牙周病', property: 't16' },
  { display: '乳牙待拔牙', property: 't17' },
  { display: '待拔牙', property: 't03' },
  { display: '贅生牙', property: 't18' },
  { display: '缺牙', property: 't02' },
  { display: '阻生牙', property: 't19' },
  { display: '口腔其他', property: 't99' },
  { display: '其他陳述', property: 't99state' },
  {
    display: '口檢表',
    property: 'tSheet',
    onSheetRender: (obj) => {
      if (!obj || typeof obj !== 'object') return '';
      return Object.entries(obj).map(([key, value]) => key + value).join('');
    }
  },
  {
    display: '複檢其他陳述',
    property: 'reT99state'
  },
  {
    display: '複檢口檢表',
    property: 'reTSheet',
    onSheetRender: (obj) => {
      if (!obj || typeof obj !== 'object') return '';
      return Object.entries(obj).map(([key, value]) => key + value).join('');
    }
  },
];


const mapState = (state: ApplicationState, ownProps: ClassSem) => ({
  ...ownProps,
});
const mapDispatch = { ...ErrorDispatches };
const connector = connect(mapState, mapDispatch);

type Props = ConnectedProps<typeof connector>;

const teeUnPassPDFPage: FunctionComponent<Props> = ({
  classId,
  className,
  year,
  sem,
  grade,
  no,
  catchErrorForModal,
}) => {
  const [filterT08, setFilterT08] = useState(false);
  const [filterT05, setFilterT05] = useState(false);
  const [filterT07, setFilterT07] = useState(false);
  const [filterT04, setFilterT04] = useState(false);
  const [filterT16, setFilterT16] = useState(false);
  const [filterT17, setFilterT17] = useState(false);
  const [filterT03, setFilterT03] = useState(false);
  const [filterT18, setFilterT18] = useState(false);
  const [filterT02, setFilterT02] = useState(false);
  const [filterT19, setFilterT19] = useState(false);
  const [originalStudents, setOriginalStudents] = useState<TeePageData[]>([]);
  const [classStudents, setClassStudents] = useState<Student[]>([]);
  const [excelStudents, setExcelStudents] = useState<PageData[]>([]);
  const [noHospitalStudents, setNoHospitalStudents] = useState<PageData[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<Set<number>>(
    new Set<number>()
  );
  // const [students, setStudents] = useState<TeePageData[]>([]);
  const [deadline, setDeadline] = useState<DateTime>();
  const [userinfo, setUser] = useState<UserInfo>();
  useEffect(() => {
    refreshStudents();
    apis.getUserInfo()
      .then((u) => {
        setUser(u);
      })
      .catch(catchErrorForModal);

    apis.GetSchoolStudentPHIUnPassTee(year, sem).then((s) => {
      const classListStudents = s.filter(t => t.tee != null).map(({ tee, ...s }, i) => ({
        ...s,
        ...tee,
        seat: s.seat || 0,
        sex: s.sex || "",
        pid: s.pid,
        sid: s.sid || "",
        index: i,
      }));
      setNoHospitalStudents(classListStudents);
    });
  }, [classId, year, sem]);


  const students = useMemo(filterTeeIssue, [
    filterT08,
    filterT05,
    filterT07,
    filterT04,
    filterT16,
    filterT17,
    filterT03,
    filterT18,
    filterT02,
    filterT19,
    selectedIndex,
    originalStudents,
  ]);


  function refreshStudents() {
    if (classId && year && sem) {
      apis.getClassStudentTee(classId, year, sem).then((s) => {
        const students = s.map(({ tee, ...s }) => ({
          ...s,
          ...tee,
        }));

        const classListStudents = students.map((s, i) => ({
          ...s,
          seat: s.seat || 0,
          sex: s.sex ?? "",
          pid: s.pid,
          sid: s.sid || "",
          index: i,
          grade: s.grade,
          no: s.no,
        }));
        setOriginalStudents(students);
        setSelectedIndex(new Set(students.map((_, i) => i)));
        setExcelStudents(classListStudents);
      });

      apis
        .getClassStudents(classId, year, sem)
        .then((s) => {
          setClassStudents(s);
        })
    } else {
      setOriginalStudents([]);
    }
  }

  function filterTeeIssue() {
    return originalStudents
      .filter((s) => {
        const hasT01 = Number(s.t01 ?? 0) === 1;
        const hasT99 = Number(s.t99 ?? 0) === 1;

        const conditions: boolean[] = [];

        if (filterT08) conditions.push(Number(s.t08 ?? 0) !== 0 && Number(s.t08 ?? 0) !== 9);
        if (filterT05) conditions.push(Number(s.t05 ?? 0) !== 0 && Number(s.t05 ?? 0) !== 9);
        if (filterT07) conditions.push(Number(s.t07 ?? 0) !== 0 && Number(s.t07 ?? 0) !== 9);
        if (filterT04) conditions.push(Number(s.t04 ?? 0) !== 0 && Number(s.t04 ?? 0) !== 9);
        if (filterT16) conditions.push(Number(s.t16 ?? 0) !== 0 && Number(s.t16 ?? 0) !== 9);
        if (filterT17) conditions.push(Number(s.t17 ?? 0) !== 0 && Number(s.t17 ?? 0) !== 9);
        if (filterT03) conditions.push(Number(s.t03 ?? 0) !== 0 && Number(s.t03 ?? 0) !== 9);
        if (filterT18) conditions.push(Number(s.t18 ?? 0) !== 0 && Number(s.t18 ?? 0) !== 9);
        if (filterT02) conditions.push(Number(s.t02 ?? 0) !== 0 && Number(s.t02 ?? 0) !== 9);
        if (filterT19) conditions.push(Number(s.t19 ?? 0) !== 0 && Number(s.t19 ?? 0) !== 9);

        if (conditions.length === 0) {
          return hasT01 || hasT99;
        }

        return hasT01 || hasT99 || conditions.some(Boolean);
      })
      .map((s, i) => ({
        ...s,
        selected: selectedIndex.has(i),
      }));
  }

  if (!classId) return <></>;

  const openPdfAfterApi = async (
    pdfPromise: Promise<Blob>,
    emptyMessage?: string
  ) => {
    try {
      const blob = await toast.promise(
        pdfPromise,
        {
          pending: '通知單產生中...',
          success: '通知單產生成功！',
          error: '通知單產生失敗！請查看錯誤資訊。',
        }
      );

      const pdfBlob = new Blob(
        [blob],
        {
          type: 'application/pdf',
        }
      );

      const pdfUrl =
        window.URL.createObjectURL(pdfBlob);

      const printWindow = window.open(
        pdfUrl,
        '_blank'
      );

      if (!printWindow) {
        toast.error(
          '瀏覽器阻擋了列印視窗，請允許彈出式視窗。'
        );

        window.URL.revokeObjectURL(
          pdfUrl
        );

        return;
      }

      setTimeout(() => {
        window.URL.revokeObjectURL(
          pdfUrl
        );
      }, 60000);
    } catch (error) {
      catchErrorForModal(error);
    }
  };

  const handleNoticePrint = () => {
    const isContentEmpty = false;

    const pids = students
      .filter((s) => s.selected)
      .map((s) => s.pid);

    const finalPids =
      pids.length > 0
        ? pids
        : students.map(
          (s) => s.pid
        );

    if (!finalPids.length) {
      toast.info(
        '目前沒有可列印的學生'
      );

      return;
    }

    openPdfAfterApi(
      apis.downloadTeePDF(
        classId,
        finalPids,
        year,
        sem,
        isContentEmpty,
        deadline
      )
    );
  };

  const handleBlankNoticePrint = () => {
    const isContentEmpty = true;

    const pids =
      originalStudents.map(
        (s) => s.pid
      );

    if (!pids.length) {
      toast.info(
        '目前沒有可列印的學生'
      );

      return;
    }

    openPdfAfterApi(
      apis.downloadTeePDF(
        classId,
        pids,
        year,
        sem,
        isContentEmpty,
        deadline
      )
    );
  };

  const footer =
    '承辦人:　　　　　　　　　　　　組長:　　　　　　　　　　　　主任:　　　　　　　　　　　　　　　　校長:　　　　　　　　　　　　';

  const classListTitle =
    `${year}學年${sem}學期_${className}口腔檢查結果清單`;

  const noHospitalTitle =
    `${year}學年${sem}學期口腔檢查未就醫清單`;

  const handleClassExcel = () => {
    downloadDataAsExcel({
      title: classListTitle,
      values: excelStudents,
      headers: excelHeader,
      footer,
      exportfile:
        userinfo?.exportFileType,
    });
  };

  const handleClassPrint = () => {
    downloadDataAsPDFByPrint({
      title: classListTitle,
      values: excelStudents,
      headers: excelHeader,
      footer,
    });
  };

  const handleNoHospitalExcel = () => {
    if (!noHospitalStudents.length) {
      toast.info(
        '無口腔檢查未就醫學生'
      );
      return;
    }

    downloadDataAsExcel({
      title: noHospitalTitle,
      values: noHospitalStudents,
      headers: excelHeader,
      footer,
      exportfile:
        userinfo?.exportFileType,
    });
  };

  const handleNoHospitalPrint = () => {
    if (!noHospitalStudents.length) {
      toast.info(
        '無口腔檢查未就醫學生'
      );
      return;
    }

    downloadDataAsPDFByPrint({
      title: noHospitalTitle,
      values: noHospitalStudents,
      headers: excelHeader,
      footer,
    });
  };

  return (
    <>
      <Row className="mb-2">
        追加條件：
        <FormCheck
          className="mr-4"
          label="牙齦炎"
          checked={filterT08}
          onChange={() => setFilterT08(!filterT08)}
        />
        <FormCheck
          className="mr-4"
          label="牙結石"
          checked={filterT05}
          onChange={() => setFilterT05(!filterT05)}
        />
        <FormCheck
          className="mr-4"
          label="咬合不正"
          checked={filterT07}
          onChange={() => setFilterT07(!filterT07)}
        />
        <FormCheck
          className="mr-4"
          label="口腔衛生不良"
          checked={filterT04}
          onChange={() => setFilterT04(!filterT04)}
        />
        <FormCheck
          className="mr-4"
          label="牙周病"
          checked={filterT16}
          onChange={() => setFilterT16(!filterT16)}
        />
        <FormCheck
          className="mr-4"
          label="乳牙待拔牙"
          checked={filterT17}
          onChange={() => setFilterT17(!filterT17)}
        />
        <FormCheck
          className="mr-4"
          label="待拔牙"
          checked={filterT03}
          onChange={() => setFilterT03(!filterT03)}
        />
        <FormCheck
          className="mr-4"
          label="贅生牙"
          checked={filterT18}
          onChange={() => setFilterT18(!filterT18)}
        />
        <FormCheck
          className="mr-4"
          label="缺牙"
          checked={filterT02}
          onChange={() => setFilterT02(!filterT02)}
        />
        <FormCheck
          className="mr-4"
          label="阻生牙"
          checked={filterT19}
          onChange={() => setFilterT19(!filterT19)}
        />
      </Row>
      <Row className="mb-2">
        繳回期限：
        <div style={{ flex: '0 0 25%' }} className="mr-2">
          <DateTimePicker
            containerClass=" px-2 py-1"
            value={deadline}
            onChange={(date) => {
              setDeadline(date);
            }}
          />
        </div>
      </Row>
      <Row className="mb-2 align-items-center">
        匯出：

        <Button
          className="ml-2"
          onClick={handleNoticePrint}
          disabled={!students.length}
        >
          通知單列印
        </Button>

        <DropdownButton
          id="tee-class-list-export"
          title="班級清單"
          variant="success"
          className="ml-2"
          disabled={!excelStudents.length}
        >
          <Dropdown.Item
            onClick={handleClassExcel}
          >
            Excel 下載
          </Dropdown.Item>

          <Dropdown.Item
            onClick={handleClassPrint}
          >
            列印
          </Dropdown.Item>
        </DropdownButton>

        <DropdownButton
          id="tee-no-hospital-export"
          title="全校未就醫清單"
          variant="success"
          className="ml-2"
          disabled={
            !noHospitalStudents.length
          }
        >
          <Dropdown.Item
            onClick={
              handleNoHospitalExcel
            }
          >
            Excel 下載
          </Dropdown.Item>

          <Dropdown.Item
            onClick={
              handleNoHospitalPrint
            }
          >
            列印
          </Dropdown.Item>
        </DropdownButton>

        <Button
          className="ml-2"
          onClick={
            handleBlankNoticePrint
          }
          disabled={
            !originalStudents.length
          }
        >
          空白通知單列印
        </Button>
      </Row>
      <Row>
        <EditableTableDiv
          headers={modalDisplayHeader}
          values={students}
          onSelected={(s) => {
            setSelectedIndex((last) => {
              if (s.index !== undefined) {
                if (s.checked) last.add(s.index);
                else last.delete(s.index);

                return new Set(last);
              } else {
                if (s.checked) return new Set(students.keys());
                else return new Set();
              }
            });
          }}
        />
      </Row>
    </>
  );
};

export const TeeUnPassPDFPage = connector(
  WrapClassBaseLayout(teeUnPassPDFPage)
);
